import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { Download, ArrowBack } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import config from "../../config/config";
import { useAuth } from "../login/AuthContext";
import { toast } from "react-toastify";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import {
  Chart as ChartJS,
  ArcElement,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Doughnut, Bar } from "react-chartjs-2";

ChartJS.register(
  ArcElement,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const SupportReport = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [reportType, setReportType] = useState("all"); // "all", "pozitif-ofis", "non-pozitif-ofis"
  const reportContentRef = React.useRef(null);
  const musteriTableRef = React.useRef(null);

  const getInitialStartDate = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, "0");
    return `${year}-${month}-01`;
  };

  const getInitialEndDate = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const day = String(new Date(year, now.getMonth() + 1, 0).getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const [startDate, setStartDate] = useState(getInitialStartDate());
  const [endDate, setEndDate] = useState(getInitialEndDate());

  const formatDate = (dateString) => {
    if (!dateString) return "";
    if (typeof dateString === "string" && dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
      const [year, month, day] = dateString.split("-");
      const monthNames = [
        "Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran",
        "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık"
      ];
      return `${day} ${monthNames[parseInt(month) - 1]} ${year}`;
    }
    const d = new Date(dateString);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    const monthNames = [
      "Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran",
      "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık"
    ];
    return `${day} ${monthNames[parseInt(month) - 1]} ${year}`;
  };

  const formatDateShort = (dateString) => {
    if (!dateString) return "";
    if (typeof dateString === "string" && dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
      const [year, month, day] = dateString.split("-");
      return `${day}.${month}.${year}`;
    }
    const d = new Date(dateString);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${day}.${month}.${year}`;
  };

  const formatDuration = (minutes) => {
    if (!minutes || minutes === 0) return "0 dk";
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0 && mins > 0) {
      return `${hours} s ${mins} dk`;
    } else if (hours > 0) {
      return `${hours} s`;
    }
    return `${mins} dk`;
  };

  const fetchReport = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${config.baseURL}/destek/rapor`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          startDate: startDate,
          endDate: endDate,
          reportType: reportType,
        },
      });

      if (response.data.success) {
        setReportData(response.data.data);
      } else {
        toast.error("Rapor alınırken hata oluştu");
      }
    } catch (error) {
      console.error("Rapor hatası:", error);
      toast.error(
        error.response?.data?.message || "Rapor alınırken hata oluştu"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReport();
  }, []);

  const handleGenerateReport = () => {
    fetchReport();
  };

  // Tablo çizme yardımcı fonksiyonu - görseldeki formata uygun
  const drawTable = (doc, startX, startY, columns, data, rowHeight = 8) => {
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 20;
    let yPos = startY;
    let currentPageStart = 0;

    // Tablo genişliği - görseldeki gibi tam genişlik
    const tableWidth = pageWidth - (startX * 2);
    // Sütun genişliklerini hesapla - eğer width belirtilmemişse eşit dağıt
    let totalFixedWidth = 0;
    let flexibleCols = 0;
    columns.forEach((col) => {
      if (col.width) {
        totalFixedWidth += col.width;
      } else {
        flexibleCols++;
      }
    });
    const flexibleWidth = flexibleCols > 0 ? (tableWidth - totalFixedWidth) / flexibleCols : 0;
    const colWidths = columns.map((col) => col.width || flexibleWidth);

    // Başlık satırı çizme fonksiyonu
    const drawHeader = (y) => {
      // Başlık satırı arka planı - gri
      doc.setFillColor(243, 244, 246); // #F3F4F6
      doc.rect(startX, y - 6, tableWidth, rowHeight, "F");

      // Başlık metinleri
      doc.setFontSize(11);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(55, 65, 81); // #374151
      let xPos = startX + 5;
      columns.forEach((col, index) => {
        doc.text(col.header, xPos, y, { align: col.align || "left" });
        xPos += colWidths[index];
      });

      // Başlık alt çizgisi
      doc.setDrawColor(209, 213, 219); // #D1D5DB
      doc.setLineWidth(0.5);
      doc.line(startX, y + 2, startX + tableWidth, y + 2);
    };

    // İlk başlık
    drawHeader(yPos);
    yPos += rowHeight + 3;

    // Veri satırları
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(17, 24, 39); // #111827

    for (let rowIndex = 0; rowIndex < data.length; rowIndex++) {
      const row = data[rowIndex];

      // Sayfa kontrolü - yeni sayfa gerekirse
      if (yPos > pageHeight - 30) {
        doc.addPage();
        yPos = margin + 10;
        drawHeader(yPos);
        yPos += rowHeight + 3;
        currentPageStart = rowIndex;
      }

      // Veri hücreleri
      let xPos = startX + 5;
      columns.forEach((col, index) => {
        let value = col.formatter
          ? col.formatter(row[col.field], row)
          : row[col.field] || "-";

        // Durum badge için özel işleme
        if (col.isStatusBadge && typeof value === "string") {
          const textWidth = doc.getTextWidth(value);
          const badgeWidth = textWidth + 10;
          const badgeHeight = 6;
          const badgeX = xPos;
          const badgeY = yPos - 5;

          // Badge arka planı (yeşil) - yuvarlatılmış köşeler için basit rect
          doc.setFillColor(16, 185, 129); // #10B981
          doc.rect(badgeX, badgeY, badgeWidth, badgeHeight, "F");

          // Badge metni (beyaz)
          doc.setTextColor(255, 255, 255);
          doc.setFontSize(9);
          doc.setFont("helvetica", "bold");
          doc.text(value, badgeX + 5, yPos - 1, { align: "left" });
          
          // Renkleri geri al
          doc.setTextColor(17, 24, 39);
          doc.setFontSize(10);
          doc.setFont("helvetica", "normal");
        } else {
          let textValue = typeof value === "string" ? value : String(value);
          // Uzun metinleri sütun genişliğine göre wrap et (maksimum 2 satır)
          const maxWidth = colWidths[index] - 10;
          const textWidth = doc.getTextWidth(textValue);
          
          if (textWidth > maxWidth) {
            const lines = doc.splitTextToSize(textValue, maxWidth);
            // İlk satırı göster
            doc.text(lines[0], xPos, yPos, { align: col.align || "left", maxWidth: maxWidth });
            // İkinci satır varsa ve yer varsa göster
            if (lines.length > 1 && yPos + 5 < pageHeight - 30) {
              doc.text(lines[1], xPos, yPos + 4, { align: col.align || "left", maxWidth: maxWidth });
              // Eğer 2 satırdan fazla varsa "..." ekle
              if (lines.length > 2) {
                doc.text("...", xPos + maxWidth - 5, yPos + 4, { align: "right" });
              }
            }
          } else {
            doc.text(textValue, xPos, yPos, { align: col.align || "left" });
          }
        }

        xPos += colWidths[index];
      });

      // Satır alt çizgisi
      doc.setDrawColor(229, 231, 235); // #E5E7EB
      doc.setLineWidth(0.3);
      doc.line(startX, yPos + 3, startX + tableWidth, yPos + 3);

      yPos += rowHeight + 2;
    }

    return { yPos, newPage: false };
  };

  const handleDownloadPDF = async () => {
    if (!reportData) {
      toast.error("Önce raporu getirin");
      return;
    }

    if (!reportContentRef.current) {
      toast.error("Rapor içeriği bulunamadı");
      return;
    }

    try {
      toast.info("PDF oluşturuluyor...");

      // Tüm scroll edilebilir tabloları bul ve maxHeight'larını kaldır
      const scrollableTables = reportContentRef.current.querySelectorAll(
        '[style*="max-height"], [style*="maxHeight"]'
      );
      const originalStyles = [];

      scrollableTables.forEach((table) => {
        const style = window.getComputedStyle(table);
        originalStyles.push({
          element: table,
          maxHeight: style.maxHeight,
          overflow: style.overflow,
        });
        // Scroll'u kaldır, tüm içeriği göster
        table.style.maxHeight = "none";
        table.style.overflow = "visible";
      });

      // TableContainer'ları da kontrol et
      const tableContainers = reportContentRef.current.querySelectorAll(
        ".MuiTableContainer-root"
      );
      tableContainers.forEach((container) => {
        const style = window.getComputedStyle(container);
        if (style.maxHeight && style.maxHeight !== "none") {
          originalStyles.push({
            element: container,
            maxHeight: style.maxHeight,
            overflow: style.overflow,
          });
          container.style.maxHeight = "none";
          container.style.overflow = "visible";
        }
      });

      // Kısa bir gecikme ekle ki DOM güncellenmesi tamamlansın
      await new Promise((resolve) => setTimeout(resolve, 100));

      // Rapor içeriğini canvas'a çevir
      const canvas = await html2canvas(reportContentRef.current, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: "#ffffff",
        width: reportContentRef.current.scrollWidth,
        height: reportContentRef.current.scrollHeight,
      });

      // Orijinal stilleri geri yükle
      originalStyles.forEach(({ element, maxHeight, overflow }) => {
        element.style.maxHeight = maxHeight;
        element.style.overflow = overflow;
      });

      const imgData = canvas.toDataURL("image/png");
      const imgWidth = 210; // A4 genişliği mm
      const pageHeight = 297; // A4 yüksekliği mm
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;

      const doc = new jsPDF("p", "mm", "a4");
      let position = 0;

      // İlk sayfayı ekle
      doc.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      // Birden fazla sayfa gerekiyorsa
      while (heightLeft > 0) {
        position = heightLeft - imgHeight;
        doc.addPage();
        doc.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      // PDF'i indir
      const fileName = `Destek_Raporu_${startDate}_${endDate}.pdf`;
      doc.save(fileName);
      toast.success("PDF başarıyla indirildi!");
    } catch (error) {
      console.error("PDF oluşturma hatası:", error);
      toast.error("PDF oluşturulurken hata oluştu");
    }
  };

  // İstatistikler
  const totalSupportCount = useMemo(() => {
    return reportData?.kullaniciDestekleri?.reduce(
      (sum, item) => sum + (item.DestekSayisi || 0),
      0
    ) || 0;
  }, [reportData]);

  const totalDuration = useMemo(() => {
    return reportData?.kullaniciDestekleri?.reduce(
      (sum, item) => sum + (item.ToplamSureDakika || 0),
      0
    ) || 0;
  }, [reportData]);

  const totalCustomers = useMemo(() => {
    return reportData?.tumMusteriler?.length || 0;
  }, [reportData]);

  const StatCard = ({ title, value, subtitle }) => (
    <Card
      elevation={2}
      sx={{
        height: "100%",
        borderLeft: "4px solid #00A7B5",
        transition: "transform 0.2s, box-shadow 0.2s",
        "&:hover": {
          transform: "translateY(-2px)",
          boxShadow: 4,
        },
      }}
    >
      <CardContent>
        <Typography
          color="textSecondary"
          gutterBottom
          variant="body2"
          fontWeight={500}
        >
          {title}
        </Typography>
        <Typography
          variant="h4"
          component="div"
          fontWeight={700}
          sx={{ color: "#1F2937", mt: 1 }}
        >
          {value}
        </Typography>
        {subtitle && (
          <Typography variant="body2" color="textSecondary" sx={{ mt: 1.5 }}>
            {subtitle}
          </Typography>
        )}
      </CardContent>
    </Card>
  );

  const ReportTable = ({ title, columns, data, keyField, maxHeight = 400 }) => {
    if (!data || data.length === 0) {
      return (
        <Card
          elevation={1}
          sx={{
            mb: 3,
            borderLeft: "4px solid #00A7B5",
          }}
        >
          <CardContent>
            <Typography variant="h6" gutterBottom fontWeight={600} sx={{ mb: 2 }}>
              {title}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Veri bulunamadı
            </Typography>
          </CardContent>
        </Card>
      );
    }

    return (
      <Card
        elevation={1}
        sx={{
          mb: 3,
          borderLeft: "4px solid #00A7B5",
        }}
      >
        <CardContent>
          <Typography
            variant="h6"
            gutterBottom
            fontWeight={600}
            sx={{ color: "#1F2937", mb: 2 }}
          >
            {title}
          </Typography>
          <TableContainer
            component={Paper}
            variant="outlined"
            sx={{
              maxHeight: maxHeight,
              border: "1px solid #E5E7EB",
              borderRadius: "8px",
              overflow: "auto",
              "&::-webkit-scrollbar": {
                width: "8px",
                height: "8px",
              },
              "&::-webkit-scrollbar-track": {
                background: "#f1f1f1",
                borderRadius: "4px",
              },
              "&::-webkit-scrollbar-thumb": {
                background: "#888",
                borderRadius: "4px",
                "&:hover": {
                  background: "#555",
                },
              },
            }}
          >
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow
                  sx={{
                    backgroundColor: "#F9FAFB",
                    "& th": {
                      borderBottom: "2px solid #E5E7EB",
                      fontWeight: 600,
                      color: "#374151",
                      backgroundColor: "#F9FAFB",
                      position: "sticky",
                      top: 0,
                      zIndex: 1,
                    },
                  }}
                >
                  {columns.map((col) => (
                    <TableCell key={col.field} align={col.align || "left"}>
                      <Typography variant="subtitle2" fontWeight={600}>
                        {col.header}
                      </Typography>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {data.map((row, idx) => (
                  <TableRow
                    key={row[keyField] || idx}
                    hover
                    sx={{
                      "&:nth-of-type(even)": {
                        backgroundColor: "#F9FAFB",
                      },
                      "&:hover": {
                        backgroundColor: "#F3F4F6",
                      },
                      "& td": {
                        borderBottom: "1px solid #E5E7EB",
                      },
                    }}
                  >
                    {columns.map((col) => (
                      <TableCell key={col.field} align={col.align || "left"}>
                        {col.formatter
                          ? col.formatter(row[col.field], row)
                          : row[col.field] || "-"}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    );
  };

  return (
    <Box
      sx={{
        height: "100vh",
        overflowY: "auto",
        overflowX: "hidden",
      }}
    >
      <Container maxWidth="xl" sx={{ py: 4 }}>
        {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 4,
        }}
      >
        <Box>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate("/destek")}
            sx={{ mb: 2 }}
          >
            Geri Dön
          </Button>
          <Typography variant="h4" fontWeight={700} sx={{ color: "#1F2937" }}>
            Destek Raporu
          </Typography>
          {reportData && (
            <Typography variant="body1" color="textSecondary" sx={{ mt: 1 }}>
              Rapor Tarihi: {formatDate(startDate)} - {formatDate(endDate)}
            </Typography>
          )}
        </Box>
        <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
          <TextField
            label="Başlangıç Tarihi"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            size="small"
            InputLabelProps={{ shrink: true }}
            sx={{ width: 180 }}
          />
          <TextField
            label="Bitiş Tarihi"
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            size="small"
            InputLabelProps={{ shrink: true }}
            sx={{ width: 180 }}
          />
          <FormControl size="small" sx={{ width: 200 }}>
            <InputLabel>Rapor Tipi</InputLabel>
            <Select
              value={reportType}
              onChange={(e) => setReportType(e.target.value)}
              label="Rapor Tipi"
            >
              <MenuItem value="all">Tümü</MenuItem>
              <MenuItem value="pozitif-ofis">Pozitif Ofis Müşterileri</MenuItem>
              <MenuItem value="non-pozitif-ofis">Diğer Müşteriler</MenuItem>
            </Select>
          </FormControl>
          <Button
            variant="contained"
            onClick={handleGenerateReport}
            disabled={loading}
            sx={{
              bgcolor: "#00A7B5",
              "&:hover": { bgcolor: "#008B98" },
            }}
          >
            RAPORU GETİR
          </Button>
          <Button
            variant="contained"
            startIcon={<Download />}
            onClick={handleDownloadPDF}
            disabled={loading}
            sx={{
              bgcolor: "#00A7B5",
              "&:hover": { bgcolor: "#008B98" },
            }}
          >
            PDF İNDİR
          </Button>
        </Box>
      </Box>

      {loading ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="400px"
        >
          <CircularProgress />
        </Box>
      ) : reportData ? (
        <Box ref={reportContentRef}>
          {/* Özet İstatistikler */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} md={4}>
              <StatCard
                title="Toplam Destek Sayısı"
                value={totalSupportCount}
                subtitle={`${formatDateShort(startDate)} - ${formatDateShort(
                  endDate
                )} arası`}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <StatCard
                title="Toplam Süre"
                value={formatDuration(totalDuration)}
                subtitle={`${totalDuration} dakika`}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <StatCard
                title="Destek Verilen Müşteri Sayısı"
                value={totalCustomers}
                subtitle="Toplam müşteri"
              />
            </Grid>
          </Grid>

          {/* Donut Chart'lar - Durum Bazlı ve Konu Bazlı */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            {/* Durum Bazlı Destek Sayıları */}
            <Grid item xs={12} md={6}>
              <Card
                elevation={1}
                sx={{
                  borderLeft: "4px solid #00A7B5",
                  height: "100%",
                }}
              >
                <CardContent>
                  <Typography
                    variant="h6"
                    gutterBottom
                    fontWeight={600}
                    sx={{ color: "#1F2937", mb: 3 }}
                  >
                    Durum Bazlı Destek Sayıları
                  </Typography>
                  {reportData.durumDestekleri &&
                  reportData.durumDestekleri.length > 0 ? (
                    <Box>
                      <Box sx={{ height: 300, mb: 3 }}>
                        <Doughnut
                          data={{
                            labels: reportData.durumDestekleri.map(
                              (item) => item.Durum || "-"
                            ),
                            datasets: [
                              {
                                data: reportData.durumDestekleri.map(
                                  (item) => item.DestekSayisi || 0
                                ),
                                backgroundColor: [
                                  "#10B981",
                                  "#3B82F6",
                                  "#EF4444",
                                  "#F59E0B",
                                  "#8B5CF6",
                                ],
                                borderWidth: 0,
                              },
                            ],
                          }}
                          options={{
                            responsive: true,
                            maintainAspectRatio: false,
                            plugins: {
                              legend: {
                                display: false,
                              },
                              tooltip: {
                                callbacks: {
                                  label: function (context) {
                                    const label =
                                      context.label || "";
                                    const value = context.parsed || 0;
                                    const item =
                                      reportData.durumDestekleri[
                                        context.dataIndex
                                      ];
                                    return `${label}: ${value} (${formatDuration(
                                      item?.ToplamSureDakika || 0
                                    )})`;
                                  },
                                },
                              },
                            },
                          }}
                        />
                      </Box>
                      <Box>
                        <Typography
                          variant="subtitle2"
                          fontWeight={600}
                          sx={{ mb: 1 }}
                        >
                          Detaylar:
                        </Typography>
                        {reportData.durumDestekleri.map((item, index) => {
                          const colors = [
                            "#10B981",
                            "#3B82F6",
                            "#EF4444",
                            "#F59E0B",
                            "#8B5CF6",
                          ];
                          return (
                            <Box
                              key={index}
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                mb: 1.5,
                              }}
                            >
                              <Box
                                sx={{
                                  width: 12,
                                  height: 12,
                                  borderRadius: "50%",
                                  backgroundColor: colors[index % colors.length],
                                  mr: 1.5,
                                }}
                              />
                              <Typography variant="body2" sx={{ flex: 1 }}>
                                <Box
                                  component="span"
                                  sx={{
                                    backgroundColor: colors[index % colors.length],
                                    color: "white",
                                    padding: "4px 12px",
                                    borderRadius: "12px",
                                    fontSize: "0.75rem",
                                    fontWeight: 500,
                                    display: "inline-block",
                                    mr: 1,
                                  }}
                                >
                                  {item.Durum || "-"}
                                </Box>
                                {item.DestekSayisi || 0} (
                                {formatDuration(item.ToplamSureDakika || 0)})
                              </Typography>
                            </Box>
                          );
                        })}
                      </Box>
                    </Box>
                  ) : (
                    <Typography variant="body2" color="textSecondary">
                      Veri bulunamadı
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Konu Bazlı Destek Süreleri */}
            <Grid item xs={12} md={6}>
              <Card
                elevation={1}
                sx={{
                  borderLeft: "4px solid #00A7B5",
                  height: "100%",
                }}
              >
                <CardContent>
                  <Typography
                    variant="h6"
                    gutterBottom
                    fontWeight={600}
                    sx={{ color: "#1F2937", mb: 3 }}
                  >
                    Konu Bazlı Destek Süreleri
                  </Typography>
                  {reportData.konuDestekleri &&
                  reportData.konuDestekleri.length > 0 ? (
                    <Box>
                      <Box sx={{ height: 300, mb: 3 }}>
                        <Doughnut
                          data={{
                            labels: reportData.konuDestekleri.map(
                              (item) => item.Konu || "-"
                            ),
                            datasets: [
                              {
                                data: reportData.konuDestekleri.map(
                                  (item) => item.ToplamSureDakika || 0
                                ),
                                backgroundColor: [
                                  "#00A7B5",
                                  "#10B981",
                                  "#F59E0B",
                                  "#3B82F6",
                                  "#EF4444",
                                  "#8B5CF6",
                                ],
                                borderWidth: 0,
                              },
                            ],
                          }}
                          options={{
                            responsive: true,
                            maintainAspectRatio: false,
                            plugins: {
                              legend: {
                                display: false,
                              },
                              tooltip: {
                                callbacks: {
                                  label: function (context) {
                                    const label = context.label || "";
                                    const value = context.parsed || 0;
                                    const item =
                                      reportData.konuDestekleri[
                                        context.dataIndex
                                      ];
                                    return `${label}: ${formatDuration(
                                      value
                                    )} (${item?.DestekSayisi || 0} destek)`;
                                  },
                                },
                              },
                            },
                          }}
                        />
                      </Box>
                      <Box>
                        <Typography
                          variant="subtitle2"
                          fontWeight={600}
                          sx={{ mb: 1 }}
                        >
                          Detaylar:
                        </Typography>
                        {reportData.konuDestekleri.map((item, index) => {
                          const colors = [
                            "#00A7B5",
                            "#10B981",
                            "#F59E0B",
                            "#3B82F6",
                            "#EF4444",
                            "#8B5CF6",
                          ];
                          return (
                            <Box
                              key={index}
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                mb: 1.5,
                              }}
                            >
                              <Box
                                sx={{
                                  width: 12,
                                  height: 12,
                                  borderRadius: "50%",
                                  backgroundColor: colors[index % colors.length],
                                  mr: 1.5,
                                }}
                              />
                              <Typography variant="body2" sx={{ flex: 1 }}>
                                {item.Konu || "-"}: {item.DestekSayisi || 0} (
                                {formatDuration(item.ToplamSureDakika || 0)})
                              </Typography>
                            </Box>
                          );
                        })}
                      </Box>
                    </Box>
                  ) : (
                    <Typography variant="body2" color="textSecondary">
                      Veri bulunamadı
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Bar Chart - Modül Bazlı Destek Süreleri */}
          {reportData.modulDestekleri &&
            reportData.modulDestekleri.length > 0 && (
              <Card
                elevation={1}
                sx={{
                  borderLeft: "4px solid #00A7B5",
                  mb: 4,
                }}
              >
                <CardContent>
                  <Typography
                    variant="h6"
                    gutterBottom
                    fontWeight={600}
                    sx={{ color: "#1F2937", mb: 3 }}
                  >
                    Modül Bazlı Destek Süreleri
                  </Typography>
                  <Box sx={{ height: 400 }}>
                    <Bar
                      data={{
                        labels: reportData.modulDestekleri.map(
                          (item) => item.Modul || "Belirtilmemiş"
                        ),
                        datasets: [
                          {
                            label: "Toplam Süre (dakika)",
                            data: reportData.modulDestekleri.map(
                              (item) => item.ToplamSureDakika || 0
                            ),
                            backgroundColor: "#00A7B5",
                            borderRadius: 4,
                          },
                        ],
                      }}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: {
                          legend: {
                            display: false,
                          },
                          tooltip: {
                            callbacks: {
                              label: function (context) {
                                const value = context.parsed.y || 0;
                                const item =
                                  reportData.modulDestekleri[context.dataIndex];
                                return `${formatDuration(value)} (${
                                  item?.DestekSayisi || 0
                                } destek)`;
                              },
                            },
                          },
                        },
                        scales: {
                          y: {
                            beginAtZero: true,
                            ticks: {
                              callback: function (value) {
                                return value;
                              },
                            },
                            grid: {
                              color: "#E5E7EB",
                            },
                          },
                          x: {
                            grid: {
                              display: false,
                            },
                            ticks: {
                              maxRotation: 45,
                              minRotation: 45,
                            },
                          },
                        },
                      }}
                    />
                  </Box>
                </CardContent>
              </Card>
            )}

          {/* Modül Bazlı Destek Süreleri - Liste */}
          {reportData.modulDestekleri &&
            reportData.modulDestekleri.length > 0 && (
              <Box sx={{ mb: 4 }}>
                <ReportTable
                  title="Modül Bazlı Destek Süreleri - Liste"
                  columns={[
                    {
                      field: "Modul",
                      header: "Modül",
                      formatter: (val) => val || "Belirtilmemiş",
                    },
                    {
                      field: "DestekSayisi",
                      header: "Destek Sayısı",
                      align: "right",
                      formatter: (val) => val || 0,
                    },
                    {
                      field: "ToplamSureDakika",
                      header: "Toplam Süre",
                      align: "right",
                      formatter: (val) => formatDuration(val || 0),
                    },
                  ]}
                  data={[...reportData.modulDestekleri].sort(
                    (a, b) => (b.ToplamSureDakika || 0) - (a.ToplamSureDakika || 0)
                  )}
                  keyField="Modul"
                  maxHeight={400}
                />
              </Box>
            )}

          {/* Rapor Tabloları */}
          <Grid container spacing={3}>
            {/* Durum Bazlı Destek Özeti */}
            <Grid item xs={12} md={6}>
              <ReportTable
                title="Durum Bazlı Destek Özeti"
                columns={[
                  {
                    field: "Durum",
                    header: "Durum",
                    formatter: (val) => {
                      const getStatusColor = (status) => {
                        switch (status) {
                          case "Destek Verildi":
                            return "#10B981";
                          case "Ar-Ge'ye iletildi":
                            return "#3B82F6";
                          case "Destek verilemedi":
                            return "#EF4444";
                          default:
                            return "#6B7280";
                        }
                      };
                      return (
                        <Box
                          component="span"
                          sx={{
                            backgroundColor: getStatusColor(val),
                            color: "white",
                            padding: "6px 16px",
                            borderRadius: "4px",
                            fontSize: "0.875rem",
                            fontWeight: 500,
                            display: "inline-block",
                          }}
                        >
                          {val}
                        </Box>
                      );
                    },
                  },
                  {
                    field: "DestekSayisi",
                    header: "Destek Sayısı",
                    align: "right",
                    formatter: (val) => val || 0,
                  },
                  {
                    field: "ToplamSureDakika",
                    header: "Toplam Süre",
                    align: "right",
                    formatter: (val) => formatDuration(val),
                  },
                ]}
                data={reportData.durumDestekleri || []}
                keyField="Durum"
                maxHeight={300}
              />
            </Grid>

            {/* Kullanıcı Bazlı Destek Özeti */}
            <Grid item xs={12} md={6}>
              <ReportTable
                title="Kullanıcı Bazlı Destek Özeti"
                columns={[
                  {
                    field: "AdSoyad",
                    header: "Kullanıcı",
                    formatter: (val, row) => {
                      if (val && val.trim() !== "" && val !== " ") {
                        return val;
                      }
                      return row.KullaniciAdi || "Belirtilmemiş";
                    },
                  },
                  {
                    field: "DestekSayisi",
                    header: "Destek Sayısı",
                    align: "right",
                    formatter: (val) => val || 0,
                  },
                  {
                    field: "ToplamSureDakika",
                    header: "Toplam Süre",
                    align: "right",
                    formatter: (val) => formatDuration(val),
                  },
                ]}
                data={reportData.kullaniciDestekleri || []}
                keyField="KullaniciId"
                maxHeight={300}
              />
            </Grid>

            {/* Destek Alan Müşteriler (Süreye Göre Sıralı) */}
            <Grid item xs={12}>
              <Box ref={musteriTableRef}>
                <ReportTable
                  title="Destek Alan Müşteriler (Süreye Göre Sıralı)"
                  columns={[
                    {
                      field: "MusteriAdi",
                      header: "Müşteri",
                    },
                    {
                      field: "DestekSayisi",
                      header: "Destek Sayısı",
                      align: "right",
                      formatter: (val) => val || 0,
                    },
                    {
                      field: "ToplamSureDakika",
                      header: "Toplam Süre",
                      align: "right",
                      formatter: (val) => formatDuration(val),
                    },
                  ]}
                  data={(reportData.tumMusteriler || []).sort(
                    (a, b) => (b.ToplamSureDakika || 0) - (a.ToplamSureDakika || 0)
                  )}
                  keyField="MusteriId"
                  maxHeight={500}
                />
              </Box>
            </Grid>
          </Grid>
        </Box>
      ) : (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="400px"
        >
          <Typography variant="body1" color="textSecondary">
            Rapor verisi bulunamadı
          </Typography>
        </Box>
      )}
      </Container>
    </Box>
  );
};

export default SupportReport;
